import { useState, type ReactNode } from 'react';
import { Bell } from 'lucide-react';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Separator } from './separator';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Skeleton } from './skeleton';
import {
  formatRelativeTime,
  notificationPath,
  notificationVisual,
  NOTIFICATION_TONE_CLASSES,
  type NotificationItem,
} from '../lib/notifications';

/**
 * The in-app notification bell + feed, shared by the member, creator and admin
 * SPAs (LMS Phase 2 put real rows in front of all three).
 *
 * PRESENTATIONAL ONLY: `packages/ui` has neither react-query nor react-router,
 * and it must stay that way — data and navigation arrive as props so each app
 * keeps its own axios instance, query keys and router. `onOpen` receives an
 * already-validated SPA-relative path; the app decides how to navigate.
 */

export function NotificationKindIcon({
  kind,
  metadata,
}: {
  kind: string;
  metadata?: Record<string, unknown>;
}) {
  const { Icon, tone } = notificationVisual(kind, metadata);
  return (
    <div
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${NOTIFICATION_TONE_CLASSES[tone]}`}
    >
      <Icon className="h-4 w-4" />
    </div>
  );
}

/**
 * One row. A row that carries a deep link renders a real `<a href>` (hover
 * URL, middle-click, screen-reader link semantics) and intercepts the click;
 * a row without one stays a button that only marks itself read — which is
 * exactly the behaviour every pre-Phase-2 kind had.
 *
 * Exported so it can be unit-tested directly: the menu's content is portalled
 * and renders nothing under `renderToStaticMarkup`, and this repo has no DOM
 * test environment.
 */
export function NotificationRow({
  item,
  variant,
  onMarkRead,
  onOpen,
  formatTime,
}: {
  item: NotificationItem;
  variant: 'menu' | 'page';
  onMarkRead: (id: string) => void;
  onOpen: (path: string) => void;
  formatTime?: (iso: string) => string;
}) {
  const path = notificationPath(item);
  const time = (formatTime ?? formatRelativeTime)(item.createdAt);
  const activate = () => {
    if (!item.readAt) onMarkRead(item.id);
    if (path) onOpen(path);
  };
  const inner =
    variant === 'menu' ? (
      <div className="flex items-start gap-2">
        {!item.readAt && (
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{item.title}</p>
          {item.body && <p className="line-clamp-2 text-xs text-muted-foreground">{item.body}</p>}
          <p className="mt-0.5 text-[10px] text-muted-foreground">{time}</p>
        </div>
      </div>
    ) : (
      <div className="flex items-start gap-3">
        <NotificationKindIcon kind={item.kind} metadata={item.metadata} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{item.title}</p>
          {item.body && <p className="mt-0.5 text-sm text-muted-foreground">{item.body}</p>}
          <p className="mt-1 text-xs text-muted-foreground">{time}</p>
        </div>
        {!item.readAt && (
          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="unread" />
        )}
      </div>
    );

  const shell =
    variant === 'menu'
      ? `block w-full cursor-pointer border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          item.readAt ? 'opacity-70' : ''
        }`
      : `-mx-3 block w-full cursor-pointer rounded-md px-3 py-3 text-left hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          item.readAt ? 'opacity-70' : ''
        }`;

  return (
    <li>
      {path ? (
        <a
          href={path}
          className={shell}
          onClick={(e) => {
            e.preventDefault();
            activate();
          }}
        >
          {inner}
        </a>
      ) : (
        <button type="button" className={shell} onClick={activate}>
          {inner}
        </button>
      )}
    </li>
  );
}

export function NotificationBellMenu({
  items,
  unreadCount,
  onMarkRead,
  onMarkAll,
  onOpen,
  seeAllHref,
  compact = false,
  align = 'end',
  markAllPending = false,
  iconClassName,
}: {
  items: NotificationItem[];
  unreadCount: number;
  onMarkRead: (id: string) => void;
  onMarkAll: () => void;
  /** Navigate — the app supplies its router's push. */
  onOpen: (path: string) => void;
  seeAllHref: string;
  compact?: boolean;
  align?: 'start' | 'end';
  markAllPending?: boolean;
  iconClassName?: string;
}) {
  // Controlled so a deep-link click can close the menu: the rows are plain
  // elements (not DropdownMenuItem), so Radix would otherwise leave the
  // portalled panel floating over the page we just navigated to.
  const [open, setOpen] = useState(false);
  const go = (path: string) => {
    setOpen(false);
    onOpen(path);
  };
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        className={`relative flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          compact ? 'h-7 w-7' : 'h-9 w-9'
        }`}
      >
        <Bell className={iconClassName ?? (compact ? 'h-[18px] w-[18px]' : 'h-4 w-4')} />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium tabular-nums leading-none text-primary-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-80">
        <div className="flex items-center justify-between px-2 pt-1">
          <DropdownMenuLabel className="px-0">Notifications</DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={markAllPending}
              onClick={() => onMarkAll()}
            >
              Mark all read
            </Button>
          )}
        </div>
        <Separator />
        {items.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            No notifications yet.
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto">
            {items.map((n) => (
              <NotificationRow
                key={n.id}
                item={n}
                variant="menu"
                onMarkRead={onMarkRead}
                onOpen={go}
              />
            ))}
          </ul>
        )}
        <Separator />
        <div className="px-2 py-1">
          <a
            href={seeAllHref}
            className="block rounded-md px-2 py-1 text-center text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={(e) => {
              e.preventDefault();
              go(seeAllHref);
            }}
          >
            See all notifications
          </a>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function NotificationFeed({
  items,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onMarkRead,
  onMarkAll,
  onOpen,
  markAllPending = false,
  formatDate,
  title = 'Notifications',
  headerActions,
}: {
  items: NotificationItem[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onMarkRead: (id: string) => void;
  onMarkAll: () => void;
  onOpen: (path: string) => void;
  markAllPending?: boolean;
  formatDate?: (iso: string) => string;
  title?: string;
  headerActions?: ReactNode;
}) {
  const anyUnread = items.some((n) => !n.readAt);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-h1">{title}</h1>
        <div className="flex items-center gap-2">
          {headerActions}
          {anyUnread && (
            <Button
              variant="outline"
              size="sm"
              disabled={markAllPending}
              onClick={() => onMarkAll()}
            >
              Mark all read
            </Button>
          )}
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Recent</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32" />
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center text-sm text-muted-foreground">
              <Bell className="mb-2 h-8 w-8 opacity-40" />
              You don&apos;t have any notifications yet.
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <NotificationRow
                  key={n.id}
                  item={n}
                  variant="page"
                  onMarkRead={onMarkRead}
                  onOpen={onOpen}
                  formatTime={formatDate}
                />
              ))}
            </ul>
          )}
          {hasNextPage && (
            <div className="pt-3 text-center">
              <Button
                variant="outline"
                size="sm"
                disabled={isFetchingNextPage}
                onClick={() => onLoadMore()}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
